import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import donationApi from "../../apis/donationApi";
import driverApi from "../../apis/driverApi";
import styles from "./IncomeReport.module.scss";
import Loading from "../../components/Loading/Loading";

const IncomeReport = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [timeFilter, setTimeFilter] = useState<"all" | "today" | "month">("month");

  const driverId = localStorage.getItem("driverId");
  const driverName = localStorage.getItem("driverName") || "Cộng tác viên";

  const fetchIncomeData = async () => {
    setLoading(true);
    try {
      let allOrders: any[] = [];
      let offset = 0;
      const limit = 100;
      let hasMore = true;

      while (hasMore) {
        const res = await donationApi.getDonations({
          filter: {
            driverId: driverId || undefined,
            statuses: ["done", "processing", "confirm", "new"],
          },
          paging: {
            limit: limit,
            offset: offset,
          },
        });

        if (res.data?.code === 0 && res.data?.data?.userDonations) {
          const list = res.data.data.userDonations;
          allOrders = [...allOrders, ...list];
          
          if (list.length < limit || !res.data.data.nextPage) {
            hasMore = false;
          } else {
            offset += limit;
          }
        } else {
          hasMore = false;
        }
      }

      setOrders(allOrders);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Không thể tải dữ liệu báo cáo thu nhập", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIncomeData();
  }, [driverId]);

  // Helper to get exact configured income for an order
  const getOrderIncome = (order: any): number => {
    try {
      if (order.driverIncome !== undefined && order.driverIncome !== null && typeof order.driverIncome === "number") {
        return order.driverIncome;
      }
      const storedConfigs = JSON.parse(localStorage.getItem("driver_order_incomes") || "{}");
      const conf = storedConfigs[order.id];
      if (conf?.calculatedIncome !== undefined && conf?.calculatedIncome !== null) {
        return Number(conf.calculatedIncome);
      }
    } catch (e) {}
    return 0;
  };

  const completedOrders = orders.filter((o) => o.status === "done");
  const inProgressOrders = orders.filter((o) => o.status === "processing" || o.status === "confirm");

  const totalCompletedIncome = completedOrders.reduce((sum, o) => sum + getOrderIncome(o), 0);
  const pendingIncome = inProgressOrders.reduce((sum, o) => sum + getOrderIncome(o), 0);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Báo cáo thu nhập</h2>
        <p className={styles.subtitle}>Xin chào {driverName}, đây là thống kê thu nhập và đơn hàng của bạn.</p>
      </div>

      <div className={styles.statsGrid}>
        <div className={styles.card}>
          <div className={styles.cardTitle}>Tổng thu nhập thực nhận</div>
          <div className={styles.cardValue} style={{ color: "#2e7d32" }}>
            {totalCompletedIncome.toLocaleString("vi")} VNĐ
          </div>
          <div className={styles.cardSub}>{completedOrders.length} đơn đã giao thành công</div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardTitle}>Thu nhập tạm tính (Đang lấy)</div>
          <div className={styles.cardValue} style={{ color: "#1976d2" }}>
            {pendingIncome.toLocaleString("vi")} VNĐ
          </div>
          <div className={styles.cardSub}>{inProgressOrders.length} đơn đang xử lý</div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardTitle}>Tổng đơn được giao</div>
          <div className={styles.cardValue} style={{ color: "#f57c00" }}>
            {orders.length}
          </div>
          <div className={styles.cardSub}>Tất cả các đơn đã nhận</div>
        </div>
      </div>

      <div className={styles.tableSection}>
        <h3>Chi tiết các đơn hàng hoàn thành</h3>
        {loading ? (
          <Loading />
        ) : completedOrders.length === 0 ? (
          <div className={styles.empty}>Chưa có đơn hàng nào hoàn thành để tính thu nhập.</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Mã đơn</th>
                <th>Khách hàng</th>
                <th>Địa chỉ lấy hàng</th>
                <th>Khối lượng</th>
                <th>Ngày hoàn thành</th>
                <th>Ảnh giao nhận</th>
                <th>Tiền công nhận</th>
              </tr>
            </thead>
            <tbody>
              {completedOrders.map((o) => {
                let proof: any = null;
                try {
                  const storedProofs = JSON.parse(localStorage.getItem("order_delivery_proofs") || "{}");
                  proof = storedProofs[o.id];
                } catch (e) {}

                return (
                  <tr key={o.id}>
                    <td><b>{o.code}</b></td>
                    <td>{o.customerName}</td>
                    <td>{o.customerAddress?.address || "N/A"}</td>
                    <td>{o.packageType || "N/A"}</td>
                    <td>{o.updatedAt ? new Date(o.updatedAt).toLocaleDateString("vi-VN") : "N/A"}</td>
                    <td>
                      {proof?.pickupImage || proof?.deliveryImage ? (
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {proof.pickupImage && (
                            <a href={proof.pickupImage} target="_blank" rel="noreferrer" title="Ảnh nhận hàng">
                              <img
                                src={proof.pickupImage}
                                alt="Nhận hàng"
                                style={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: 6,
                                  objectFit: "cover",
                                  border: "1px solid #16a34a",
                                }}
                              />
                            </a>
                          )}
                          {proof.deliveryImage && (
                            <a href={proof.deliveryImage} target="_blank" rel="noreferrer" title="Ảnh trả hàng">
                              <img
                                src={proof.deliveryImage}
                                alt="Trả hàng"
                                style={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: 6,
                                  objectFit: "cover",
                                  border: "1px solid #2563eb",
                                }}
                              />
                            </a>
                          )}
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>Không có ảnh</span>
                      )}
                    </td>
                    <td style={{ color: "#2e7d32", fontWeight: "bold" }}>
                      +{getOrderIncome(o).toLocaleString("vi")} VNĐ
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default IncomeReport;
